import React from "react";
import dayjs from "dayjs";
import {
  Button, Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { BODY_CELL, HEAD_CELL } from "./confiscationStyles";

const HEADERS = ["Date", "Demandeur", "Cible", "Motif", "Montant", "Actions"];

/**
 * Builds a human label for the confiscation target (user or group).
 * @param {Object} req - The confiscation request
 * @returns {string} A short target label
 */
function targetLabel(req) {
  return req.target_group_id ? `Groupe #${req.target_group_id}` : `Utilisateur #${req.target_user_id}`;
}

/**
 * Formats the wallet balance that would be confiscated.
 * @param {Object} req - The confiscation request
 * @returns {string} The formatted amount or a dash
 */
function amountLabel(req) {
  return req.wallet_balance != null ? Number(req.wallet_balance).toFixed(2) : "—";
}

/**
 * Renders a single confiscation request row with approve/reject actions.
 * @param {Object} props - req, onApprove, onReject
 * @returns {React.ReactElement}
 */
function ConfiscationRow({ req, onApprove, onReject }) {
  const color = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Tr>
      <Td {...BODY_CELL} color={color}>{dayjs(req.created_at).format("DD/MM/YYYY HH:mm")}</Td>
      <Td {...BODY_CELL} color={color}>{req.requester_admin_email || `#${req.requester_admin_id}`}</Td>
      <Td {...BODY_CELL} color={color}>{targetLabel(req)}</Td>
      <Td {...BODY_CELL} color={color} maxW="280px" whiteSpace="normal">{req.reason}</Td>
      <Td {...BODY_CELL} color={color}>{amountLabel(req)}</Td>
      <Td {...BODY_CELL} textAlign="right">
        <Flex gap="8px" justify="flex-end">
          <Button size="sm" colorScheme="green" variant="outline" onClick={() => onApprove(req)}>Approuver</Button>
          <Button size="sm" colorScheme="red" variant="outline" onClick={() => onReject(req)}>Rejeter</Button>
        </Flex>
      </Td>
    </Tr>
  );
}

/**
 * Renders the table of pending confiscation requests, or an empty state.
 * @param {Object} props - items, onApprove, onReject
 * @returns {React.ReactElement}
 */
function ConfiscationTable({ items, onApprove, onReject }) {
  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  if (!items.length) {
    return (
      <Card>
        <Text textAlign="center" py="20px" fontSize="sm" color={emptyColor}>
          Aucune demande de confiscation en attente.
        </Text>
      </Card>
    );
  }
  return (
    <Card px="0" py="0" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              {HEADERS.map((h) => (
                <Th key={h} {...HEAD_CELL} color={headColor} textAlign={h === "Actions" ? "right" : "left"}>{h}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {items.map((req) => (
              <ConfiscationRow key={req.id} req={req} onApprove={onApprove} onReject={onReject} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Card>
  );
}

export default ConfiscationTable;
